import {
    IClientSdkConfig,
    IUrls,
    IWebSDK,
    IWebSDKApplicationUrls,
    IWebSDKDomains,
    IWebSDKInitPayload,
    IWebSDKThirdParty,
} from './types';
import { domainOnboarding } from '@sxm-de/dev-kits/web-sdk/domains/onboarding';
import { domainAccount } from '@sxm-de/dev-kits/web-sdk/domains/account';
import { domainAuth } from '@sxm-de/dev-kits/web-sdk/domains/auth';
import {
    IAnalytics,
    analytics,
} from '@sxm-de/dev-kits/web-sdk/domains/analytics';
import { domainAcquisitions } from '@sxm-de/dev-kits/web-sdk/domains/acquisitions';
import {
    featureFlags,
    IFeatureFlags,
} from '@sxm-de/dev-kits/web-sdk/domains/feature-flags';
import {
    campaignFlags,
    ICampaignFlags,
} from '@sxm-de/dev-kits/web-sdk/domains/campaign-flags';
import { domainSubscriptions } from '@sxm-de/dev-kits/web-sdk/domains/subscriptions';
import {
    IWebSDKCountryCodes,
    IWebSDKGeoLocation,
    IWebSDKLocale,
    IWebSdkState,
    DependenciesPayload,
} from '@sxm-de/dev-kits/web-sdk/types';
import {
    getGeoLocationFromLocation,
    logInfoMessage,
} from '@sxm-de/dev-kits/web-sdk/utils';
import { ContentfulSdk } from '@sxm-de/dev-kits/contentful-sdk/core';
import { MicroservicesSdk } from '@sxm-de/dev-kits/microservices-sdk/core';
import { AtlasSdk } from '@sxm-de/dev-kits/atlas-sdk/core';
import { ErrorLoggerSdkMicroservices } from './error-logging/error-logger-sdk-microservices';

export class WebSDK implements IWebSDK {
    domains!: IWebSDKDomains;
    analytics!: IAnalytics;
    urls: IUrls = {
        toPlayerAppBaseUrl: '',
        toPlayerAppCanada: '',
        toPlayerAppUS: '',
        toPlayerApp: '',
        careBaseUrl: '',
        accountCenterBaseUrl: '',
        onboardingClaimTrialBaseUrl: '',
        acquisitionsSelfPayBaseUrl: '',
        vanityBaseUrl: '',
    };
    featureFlags!: IFeatureFlags;
    campaignFlags!: ICampaignFlags;
    private appUrls!: IWebSDKApplicationUrls;
    countryCodeDefault!: IWebSDKCountryCodes;
    geoLocation!: IWebSDKGeoLocation;
    private locale!: IWebSDKLocale;
    private thirdParty!: IWebSDKThirdParty;
    /**
     * @deprecated
     */
    // TODO: Refine to standard state management. This should not be used.
    state!: IWebSdkState;
    private clientSdkConfig!: IClientSdkConfig;

    init({ initDependencies, settings }: IWebSDKInitPayload) {
        if (!this.clientSdkConfig) {
            this.clientSdkConfig = settings.clientSdkConfig;
        }

        const microservicesSdk = new MicroservicesSdk({
            baseUrlUS: settings.microservices.baseUrlUS || '',
            baseUrlCanada: settings.microservices.baseUrlCanada || '',
            currentCountryCode: settings.countryCode,
        });
        const errorLoggerSdk = new ErrorLoggerSdkMicroservices(
            microservicesSdk,
        );
        const dependenciesPayload: DependenciesPayload = {
            ...initDependencies,
            contentfulSdk: new ContentfulSdk({
                baseUrl: this.clientSdkConfig.baseUrlContentful || '',
                accessToken: this.clientSdkConfig.contentfulAccessToken || '',
                spaceId: this.clientSdkConfig.contentfulSpaceId || '',
                environmentId:
                    this.clientSdkConfig.contentfulEnvironmentId || '',
            }),
            microservicesSdk,
            atlasSdk: new AtlasSdk({
                baseUrl: this.clientSdkConfig.baseUrl,
                clientSdk: initDependencies.clientSdk,
            }),
            errorLoggerSdk,
        };

        if (!this.analytics) {
            this.analytics = analytics(dependenciesPayload);
        }
        if (!this.countryCodeDefault) {
            this.countryCodeDefault = settings.countryCode;
        }
        if (!this.geoLocation) {
            const authToken =
                dependenciesPayload.clientSdkStorageProvider?.getItem(
                    'AUTH_TOKEN',
                ) as any;
            const location =
                authToken &&
                (authToken?.['session']?.['location'] as {
                    region: string;
                    countryCode: string;
                });
            this.geoLocation = getGeoLocationFromLocation(location) || {
                countryCode: '',
                region: '',
                regionCode: '',
            };
        }
        if (!this.appUrls) {
            this.appUrls = settings.appUrls;
        }
        if (!this.locale) {
            this.locale = settings.locale;
        }
        if (!this.thirdParty) {
            this.thirdParty = settings.thirdParty;
        }
        if (!this.state) {
            this.state = {};
        }

        this.urls.toPlayerAppBaseUrl = this.appUrls.branchUrls?.baseUrl || '';
        this.urls.toPlayerAppCanada =
            `${this.urls.toPlayerAppBaseUrl}/${this.appUrls.branchUrls?.toPlayerAppCanada}` ||
            '';
        this.urls.toPlayerAppUS =
            `${this.urls.toPlayerAppBaseUrl}/${this.appUrls.branchUrls?.toPlayerApp}` ||
            '';
        this.urls.toPlayerApp =
            this.countryCodeDefault === 'ca'
                ? this.urls.toPlayerAppCanada
                : this.urls.toPlayerAppUS;
        this.urls.careBaseUrl =
            this.countryCodeDefault === 'ca'
                ? this.appUrls.careCanadaBaseUrl
                : this.appUrls.careUSBaseUrl;
        this.urls.accountCenterBaseUrl =
            this.countryCodeDefault === 'ca'
                ? this.appUrls.accountCenterCanadaBaseUrl
                : this.appUrls.accountCenterUSBaseUrl;
        this.urls.onboardingClaimTrialBaseUrl =
            this.countryCodeDefault === 'ca'
                ? this.appUrls.onboardingClaimTrialCanadaBaseUrl
                : this.appUrls.onboardingClaimTrialUSBaseUrl;
        this.urls.acquisitionsSelfPayBaseUrl =
            this.countryCodeDefault === 'ca'
                ? this.appUrls.acquisitionsSelfPayCanadaBaseUrl
                : this.appUrls.acquisitionsSelfPayUSBaseUrl;
        this.urls.vanityBaseUrl =
            this.countryCodeDefault === 'ca'
                ? this.appUrls.vanityUrls.baseUrlCanada
                : this.appUrls.vanityUrls.baseUrl;

        if (!this.domains) {
            const domainInitPayload = {
                dependencies: dependenciesPayload,
                countryCodeDefault: this.countryCodeDefault,
                locale: this.locale,
                state: this.state,
            };
            this.domains = {
                account: domainAccount({
                    ...domainInitPayload,
                    clientSdkBaseUrl: this.clientSdkConfig.baseUrl,
                }),
                auth: domainAuth(domainInitPayload),
                onboarding: domainOnboarding(domainInitPayload),
                acquisitions: domainAcquisitions(domainInitPayload),
                subscriptions: domainSubscriptions(domainInitPayload),
            };
            if (!this.thirdParty?.oneTrust?.disabled) {
                this.domains.account.privacyOptionsOptOut();
            }
        }
        if (!this.featureFlags) {
            this.featureFlags = featureFlags({
                featureFlags: settings.featureFlags,
            });
        }

        if (!this.campaignFlags) {
            if (!this.thirdParty.salesforce.disabled) {
                this.campaignFlags = campaignFlags(
                    this.thirdParty.salesforce.environment,
                    dependenciesPayload,
                );
            } else {
                this.campaignFlags = {
                    getCampaignFlag: async () => {
                        throw new Error('Campaign flag provider disabled.');
                    },
                    captureCampaignFlagImpression: async () => {
                        throw new Error('Campaign flag provider disabled.');
                    },
                };
            }
        }
        logInfoMessage('App started', { errorLoggerSdk });
    }
}
